/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController, NavParams, ToastController } from '@ionic/angular';
import { ComplimentModalComponent } from '@shared/components/compliment-modal/compliment-modal.component';
import { MediaService } from '@shared/services/media.service';
import { UserService } from '@shared/services/user.service';
import { UtilService } from '@shared/utils/util';
import { TranslateService } from '@ngx-translate/core';
import { VoiceRecorder, GenericResponse } from 'capacitor-voice-recorder';
import { environment } from 'src/environments/environment';

/**
 * Elogio (Compliment, estilo Bumble): modal para escrever uma mensagem curta para um perfil.
 * Sem match: 1 spark + auto-like no servidor. Com match: grátis.
 *
 * O envio real é feito no SERVIDOR — este modal coleta o conteúdo e devolve; o chamador
 * (home/profile-display) chama sendMessage(..., compliment=true) e trata as respostas.
 *
 * Sem match e sem moeda: abre o modal de compra sem sair desta tela.
 *
 * SK-50: além de texto, permite gravar e enviar um ÁUDIO como elogio — reaproveitando a mesma
 * infra do chat (MediaService + capacitor-voice-recorder). O áudio é subido via
 * userService.uploadAudio e devolvido como corpo JSON {msgType:'audio',...} para o chamador
 * mandar pelo mesmo endpoint (compliment=true).
 */
@Component({
  selector: 'app-recadinho',
  templateUrl: './recadinho.page.html',
  styleUrls: ['./recadinho.page.scss'],
})
export class RecadinhoPage implements OnInit, OnDestroy {
  public myForm: FormGroup;
  public theSoul: any;
  public soulName: string;
  /** Se o usuário já curtiu esse perfil, o CTA vira só "Enviar..." (sem "Curtir e"). */
  public alreadyLiked: boolean = false;
  /** Já é match: elogio grátis (sem cobrança nem checagem de saldo). */
  public isMatched: boolean = false;
  /** Foto do remetente (avatar na linha do input, como no Bumble). */
  public myPic: string | null = null;
  /** Miniatura da foto alvo do elogio (a que está sendo elogiada). */
  public photoUrl: string | null = null;
  /** Meus dados (para checar saldo e abrir a loja). */
  public me: any = null;
  /** ObjectId (pictureId) da foto alvo do elogio, repassado ao backend no envio. */
  public photoId: any = null;

  // --- Áudio (SK-50) ---
  /** Gravando agora. */
  public isRecording: boolean = false;
  /** Já tem um áudio gravado pronto pra enviar. */
  public hasAudio: boolean = false;
  /** Segundos restantes na gravação (conta regressiva a partir de MAX_AUDIO_SECS). */
  public recElapsed: number = 0;
  /** Duração do áudio gravado (s). */
  public audioDuration: number = 0;
  /** Subindo o áudio no envio. */
  public uploading: boolean = false;
  private audioUri: string | null = null;
  private audioFileName: string | null = null;
  private static readonly MAX_AUDIO_SECS = 60;
  private micRequests: number = 0;

  private readonly price: number = environment.priceSuperLike ?? 1;
  private static readonly MAX_CHARS = 1000;

  constructor(
    public modalCtrl: ModalController,
    public navParams: NavParams,
    private media: MediaService,
    private userService: UserService,
    private util: UtilService,
    private translate: TranslateService,
    private toastCtrl: ToastController,
  ) {
    this.theSoul = navParams.get('theSoul');
    this.soulName = this.theSoul?.firstName != null ? this.theSoul.firstName : '';
    this.isMatched = !!navParams.get('isMatched') || !!this.theSoul?.match;
    this.alreadyLiked = !!navParams.get('alreadyLiked') || this.isMatched;
    this.myPic = navParams.get('myPic') ?? null;
    this.me = navParams.get('me') ?? null;
    this.photoId = navParams.get('photoId') ?? null;
    this.photoUrl = navParams.get('photoUrl') ?? this.theSoul?.firstPic ?? null;

    this.myForm = new FormGroup({
      // Sem Validators.required: o elogio pode ser só áudio. O canSend() garante
      // que há texto OU áudio antes de habilitar o envio.
      msgControl: new FormControl('', [Validators.maxLength(RecadinhoPage.MAX_CHARS)]),
    });
  }

  ngOnInit() {}

  getCharsLeft(): number {
    const value: string = this.myForm.controls['msgControl'].value ?? '';
    return RecadinhoPage.MAX_CHARS - value.length;
  }

  private get textValue(): string {
    return (this.myForm.controls['msgControl'].value ?? '').trim();
  }

  /** Há texto digitado? O template alterna microfone/enviar por isto, igual ao
   *  chat (chat.page.html:235-242 usa theMsgControl.value.length). */
  get hasText(): boolean {
    return this.textValue.length > 0;
  }

  canSend(): boolean {
    if (this.uploading) {
      return false;
    }
    // Gravando: o enviar fica ATIVO e vale por "parar e enviar", igual ao chat
    // (chat.page.html:270 chama stopRecord(), que ja despacha o audio).
    // Antes exigia parar primeiro — um toque a mais, que era a reclamacao.
    // O botao de stop continua ali para quem quiser ouvir/descartar antes.
    if (this.isRecording) {
      return true;
    }
    return this.myForm.valid && (this.textValue.length > 0 || this.hasAudio);
  }

  // ---- Áudio (SK-50) ----
  async toggleRecord() {
    if (this.isRecording) {
      await this.stopRecord();
    } else {
      await this.startRecord();
    }
  }

  private async startRecord() {
    if (this.hasAudio) {
      return; // já tem um áudio; usuário precisa descartar antes de regravar
    }
    const perm: GenericResponse = await VoiceRecorder.hasAudioRecordingPermission();
    if (perm.value !== true) {
      const req: GenericResponse = await VoiceRecorder.requestAudioRecordingPermission();
      if (req.value !== true) {
        this.micRequests++;
        if (this.micRequests >= 2) {
          this.util.presentToast(
            this.toastCtrl,
            this.translate.instant('pleaseAuthorizeMicrophoneAccess'),
            3000,
          );
        }
        return;
      }
    }

    this.media.startRecord();
    this.isRecording = true;
    this.recElapsed = RecadinhoPage.MAX_AUDIO_SECS;
    this.tickRecord();
  }

  private tickRecord() {
    setTimeout(() => {
      if (!this.isRecording) {
        return;
      }
      if (this.recElapsed <= 1) {
        void this.stopRecord(); // atinge o limite -> para sozinho
        return;
      }
      this.recElapsed -= 1;
      this.tickRecord();
    }, 1000);
  }

  private async stopRecord() {
    // Idempotente: o limite de MAX_AUDIO_SECS para sozinho pelo tickRecord() e o
    // usuario pode tocar em enviar/stop no mesmo instante. Sem esta guarda, o
    // segundo chamador pediria stopRecord() a um gravador ja parado.
    if (!this.isRecording) {
      return;
    }
    this.isRecording = false;
    const uri = await this.media.stopRecord();
    // O caminho do arquivo pode vir vazio e a gravacao existir: na web o
    // uploadAudio ignora o caminho e manda a base64 (user.service.ts:1610-1612).
    // Exigir `uri` aqui fazia o chip nunca aparecer nesse caso, e a gravacao se
    // perdia sem aviso nenhum.
    if (uri || this.media.lastRecordBase64) {
      this.audioUri = uri ?? null;
      this.audioFileName = this.media.fileName;
      this.audioDuration = this.media.duration ?? 0;
      this.hasAudio = true;
    }
  }

  /** Tocando a prévia? O template liga aqui em vez de alcançar o serviço. */
  get isPreviewPlaying(): boolean {
    return this.media.previewPlaying;
  }

  /** Ouvir a gravação antes de enviar; tocar de novo pausa. */
  togglePreview(): void {
    this.media.togglePreview();
  }

  ngOnDestroy(): void {
    // O modal pode fechar com a prévia tocando (enviar, X, gesto de voltar).
    // Sem isto o áudio continuaria tocando por cima da tela seguinte.
    this.media.stopPreview();
  }

  discardAudio() {
    this.media.stopPreview();
    this.audioUri = null;
    this.audioFileName = null;
    this.audioDuration = 0;
    this.hasAudio = false;
  }

  dismiss() {
    this.modalCtrl.dismiss('cancelled');
  }

  async send() {
    // Ainda gravando: encerra antes, para o toque valer por parar + enviar.
    // stopRecord() liga hasAudio, entao o canSend() abaixo reavalia ja com o
    // audio pronto; se a gravacao nao produzir arquivo, cai fora sem enviar.
    if (this.isRecording) {
      await this.stopRecord();
    }
    this.media.stopPreview();
    if (!this.canSend()) {
      return;
    }

    // Com match o elogio é grátis. Sem match e sem saldo: modal de compra
    // (formato Super Like) por cima, sem sair desta tela.
    if (!this.isMatched && (this.me?.coins ?? 0) < this.price) {
      const modal = await this.modalCtrl.create({
        component: ComplimentModalComponent,
        componentProps: { userData: this.me, theSoul: this.theSoul },
        cssClass: 'custom-class-modal',
        showBackdrop: true,
        backdropDismiss: true,
      });
      await modal.present();
      return;
    }

    // Elogio em ÁUDIO: sobe o arquivo e devolve o corpo JSON pronto (mesmo formato do chat),
    // pra o chamador enviar por sendMessage(..., recadinho=true).
    // Basta hasAudio: exigir audioUri tambem fazia o elogio em AUDIO cair no ramo
    // de TEXTO quando o caminho vinha vazio — gastando uma cenoura num elogio
    // vazio. O uploadAudio aceita caminho vazio quando ha base64.
    if (this.hasAudio) {
      this.uploading = true;
      try {
        const result = await this.userService.uploadAudio(
          this.audioUri ?? '',
          this.me,
          this.audioFileName ?? 'recadinho-audio',
          { base64: this.media.lastRecordBase64, mimeType: this.media.lastRecordMimeType },
        );
        // Objeto (não string): o HttpClient serializa como JSON igual ao chat faz
        // no envio de áudio, pra o backend receber o mesmo formato de mensagem.
        const audioBody = {
          msgType: 'audio',
          filename: this.audioFileName,
          duration: this.audioDuration,
          fileId: result.response,
        };
        this.uploading = false;
        this.modalCtrl.dismiss({ recadinho: audioBody, photoId: this.photoId, msgType: 'audio' });
      } catch (error) {
        this.uploading = false;
        this.util.showTopToastUnableToConnect(this.toastCtrl);
        console.log('Erro ao subir áudio do elogio: ' + JSON.stringify(error));
      }
      return;
    }

    // Elogio em TEXTO (comportamento original).
    this.modalCtrl.dismiss({ recadinho: this.textValue, photoId: this.photoId });
  }
}
